import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Plus, Download } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { useCasting } from "@/context/CastingContext";
import { useLanguage } from "@/context/LanguageContext";
import { AudioPlayer } from "@/components/AudioPlayer";

const MODEL_STATS = [
  ["height", { en: "Height", fr: "Taille" }],
  ["bust_chest", { en: "Bust / Chest", fr: "Buste / Poitrine" }],
  ["waist", { en: "Waist", fr: "Taille (ceinture)" }],
  ["hips", { en: "Hips", fr: "Hanches" }],
  ["shoes", { en: "Shoes", fr: "Pointure" }],
  ["eyes", { en: "Eyes", fr: "Yeux" }],
  ["hair", { en: "Hair", fr: "Cheveux" }],
];

const List = ({ label, items, testid }) =>
  items && items.length > 0 ? (
    <div data-testid={testid} className="mt-8">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{label}</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {items.map((x) => (
          <span key={x} className="border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/75">
            {x}
          </span>
        ))}
      </div>
    </div>
  ) : null;

export default function TalentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { has, toggle } = useCasting();
  const { t, tCat, lang } = useLanguage();
  const [talent, setTalent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);

  useSEO(
    talent ? `${talent.name} — Diamotims Agency` : "Talent — Diamotims Agency",
    talent ? `${talent.name} — ${(talent.categories || []).join(", ")} — Diamotims Agency, Montréal.` : undefined
  );

  useEffect(() => {
    setTalent(null);
    setImgIdx(0);
    api
      .get(`/talent/${id}`)
      .then((r) => setTalent(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <main className="flex min-h-screen flex-col items-center justify-center px-4 pt-24 text-center">
        <h1 className="font-display text-4xl font-light uppercase text-ivory">{t("profile.notFound")}</h1>
        <Link to="/discover" data-testid="profile-back-discover" className="mt-6 border border-white/30 px-6 py-3 font-mono text-xs uppercase tracking-[0.3em] text-white hover:border-gold hover:text-gold">
          {t("home.discover")}
        </Link>
      </main>
    );

  if (!talent)
    return (
      <main className="flex min-h-screen items-center justify-center font-mono text-xs uppercase tracking-[0.3em] text-white/40">
        {t("profile.loading")}
      </main>
    );

  const images = [talent.image_url, ...(talent.gallery || [])].filter(Boolean);
  const active = has(talent.id);
  const divLabel = { MODELS: t("footer.models"), ACTORS: t("footer.actors"), VOICE: t("footer.voice") }[talent.division] || talent.division;

  return (
    <main data-testid="talent-profile-page" className="pt-24 lg:pt-32">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="mx-auto max-w-[1800px] px-4 pb-24 sm:px-8 lg:px-16"
      >
        <button
          onClick={() => navigate(-1)}
          data-testid="profile-back-button"
          className="mb-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 transition-colors hover:text-gold"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {t("profile.back")}
        </button>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <div className="relative overflow-hidden border border-white/10 bg-[#121212]">
              <img
                key={images[imgIdx]}
                src={images[imgIdx]}
                alt={`${talent.name} — ${imgIdx + 1}`}
                className="aspect-[3/4] w-full object-cover object-top"
              />
              {talent.new_face && (
                <span className="absolute left-4 top-4 border border-gold/50 bg-black/50 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-gold backdrop-blur-md">
                  {t("profile.newFace")}
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {images.map((src, i) => (
                  <button
                    key={i}
                    data-testid={`profile-thumb-${i}`}
                    aria-label={`Photo ${i + 1}`}
                    onClick={() => setImgIdx(i)}
                    className={`h-20 w-16 overflow-hidden border transition-opacity ${i === imgIdx ? "border-gold" : "border-white/20 opacity-60 hover:opacity-100"}`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover object-top" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              {divLabel} — {(talent.categories || []).map(tCat).join(" / ")}
            </div>
            <h1 className="mt-3 font-display text-4xl font-light uppercase leading-[0.95] tracking-tight text-ivory sm:text-6xl">
              {talent.name}
            </h1>
            <div className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-white/50">{talent.location}</div>

            {talent.bio && (
              <p className="mt-8 max-w-xl font-display text-xl font-light italic leading-relaxed text-white/75">
                {talent.bio}
              </p>
            )}

            {talent.division === "MODELS" && (
              <dl data-testid="profile-measurements" className="mt-8 divide-y divide-white/10 border-y border-white/10">
                {MODEL_STATS.filter(([k]) => talent[k]).map(([k, labels]) => (
                  <div key={k} className="flex items-center justify-between py-2.5">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">{labels[lang] || labels.en}</dt>
                    <dd className="font-mono text-xs text-white/90">{talent[k]}</dd>
                  </div>
                ))}
                <div className="flex items-center justify-between py-2.5">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">{t("profile.representation")}</dt>
                  <dd className="font-mono text-xs text-white/90">{t("profile.repValue")}</dd>
                </div>
              </dl>
            )}

            {talent.division === "ACTORS" && (
              <div className="mt-8 space-y-6">
                {talent.union_status && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t("profile.union")}</div>
                    <div className="mt-2 font-mono text-xs text-white/85">{talent.union_status}</div>
                  </div>
                )}
                {talent.training && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t("profile.training")}</div>
                    <div className="mt-2 text-sm text-white/70">{talent.training}</div>
                  </div>
                )}
                {talent.credits && talent.credits.length > 0 && (
                  <div data-testid="profile-credits">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t("profile.credits")}</div>
                    <ul className="mt-2 space-y-1.5">
                      {talent.credits.map((c) => (
                        <li key={c} className="border-l border-gold/40 pl-3 text-sm text-white/75">{c}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {talent.division === "VOICE" && talent.vocal_range && (
              <div className="mt-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t("profile.vocal")}</div>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{talent.vocal_range}</p>
              </div>
            )}

            <List label={t("profile.languages")} items={talent.languages} testid="profile-languages" />
            <List label={t("profile.accents")} items={talent.accents} testid="profile-accents" />
            <List label={t("profile.skills")} items={talent.skills} testid="profile-skills" />

            {talent.demos && talent.demos.length > 0 && (
              <div className="mt-10">
                <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t("profile.demos")}</div>
                <div className="space-y-3">
                  {talent.demos.map((d, i) => (
                    <AudioPlayer key={i} src={d.url} title={d.title} category={d.category} testid={`voice-audio-${talent.id}-${i}`} />
                  ))}
                </div>
                <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-white/30">
                  {t("profile.demosNote")}
                </p>
              </div>
            )}

            <div className="mt-auto flex flex-col gap-3 pt-10">
              <div className="flex gap-3">
                <button
                  data-testid="profile-shortlist-toggle"
                  onClick={() => toggle(talent)}
                  className={`flex flex-1 items-center justify-center gap-2 border px-4 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${
                    active ? "border-gold bg-gold text-black" : "border-white/20 text-white hover:border-gold hover:text-gold"
                  }`}
                >
                  {active ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {active ? t("profile.shortlisted") : t("profile.addShortlist")}
                </button>
                <a
                  data-testid="profile-compcard-download"
                  href={talent.image_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("profile.openPortrait")}
                  className="flex w-12 items-center justify-center border border-white/20 text-white transition-colors hover:border-gold hover:text-gold"
                >
                  <Download className="h-4 w-4" />
                </a>
              </div>
              <button
                data-testid="profile-enquire-button"
                onClick={() => {
                  if (!active) toggle(talent);
                  navigate("/clients");
                }}
                className="group flex items-center justify-center gap-3 bg-ivory px-6 py-4 font-mono text-xs uppercase tracking-[0.3em] text-black transition-colors hover:bg-gold"
              >
                {talent.division === "VOICE" ? t("profile.enquireVoice") : t("profile.enquire")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </main>
  );
}
